import React, { ReactNode, useCallback, useEffect, useState } from "react";
import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AppBar from "@material-ui/core/AppBar";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Close from "@material-ui/icons/Close";

export type Option = { id: string; title: string; active: boolean };

export type NestedOption = { title: string; data: Option[] };
export interface Options {
  title: string;
  options: Option[];
  nestedList?: NestedOption[];
}

function findOptionById(optionGroups: Options[], id: string | undefined): Option | null {
  if (!id) return null;
  for (const g of optionGroups) {
    for (const o of g.options) {
      if (o.id === id) return o;
    }
    if (g.nestedList) {
      for (const nest of g.nestedList) {
        for (const o of nest.data) {
          if (o.id === id) return o;
        }
      }
    }
  }
  return null;
}

const useStyles = makeStyles((theme) => ({
  appBar: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  toolbar: {
    minHeight: 48,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  title: {
    flex: 1,
    fontWeight: 600,
    marginLeft: theme.spacing(0.5),
  },
  content: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "hidden",
    paddingTop: theme.spacing(1.5),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      paddingLeft: "max(16px, env(safe-area-inset-left, 0px))",
      paddingRight: "max(16px, env(safe-area-inset-right, 0px))",
      gap: theme.spacing(0.75),
    },
  },
  accordionStack: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    minHeight: 0,
    gap: theme.spacing(1),
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(0.75),
    },
  },
  actions: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.5, 2),
    paddingBottom: `max(${theme.spacing(1.5)}px, env(safe-area-inset-bottom, 0px))`,
    paddingLeft: "max(16px, env(safe-area-inset-left, 0px))",
    paddingRight: "max(16px, env(safe-area-inset-right, 0px))",
    justifyContent: "space-between",
  },
  trigger: {
    justifyContent: "flex-start",
    textTransform: "none" as const,
    minHeight: 48,
    borderRadius: theme.shape.borderRadius,
  },
  paperDesktop: {
    [theme.breakpoints.up("md")]: {
      maxHeight: "min(80vh, 720px)",
    },
  },
  accordion: {
    width: "100%",
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    overflow: "hidden",
    transition: "border-color 180ms ease, box-shadow 180ms ease",
    "&.Mui-expanded": {
      margin: 0,
      borderColor: theme.palette.primary.main,
      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
    },
    "&:first-child": {
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
    },
    "&:last-child": {
      borderBottomLeftRadius: 16,
      borderBottomRightRadius: 16,
    },
    "&:before": {
      display: "none",
    },
    [theme.breakpoints.up("md")]: {
      minWidth: 320,
    },
  },
  accordionExpanded: {
    flex: "0 1 auto",
    maxHeight: "72%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    "& .MuiCollapse-root": {
      flex: "1 1 auto",
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
    },
    "& .MuiCollapse-wrapper": {
      flex: "1 1 auto",
    },
    "& .MuiCollapse-wrapperInner": {
      height: "100%",
    },
  },
  accordionSummary: {
    minHeight: 52,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1.25),
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down("sm")]: {
      minHeight: 48,
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(0.75),
    },
    "&.Mui-expanded": {
      minHeight: 52,
      backgroundColor: "rgba(63, 81, 181, 0.04)",
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    "& .MuiAccordionSummary-content": {
      margin: theme.spacing(1, 0),
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
    },
    "& .MuiAccordionSummary-content.Mui-expanded": {
      margin: theme.spacing(1, 0),
    },
  },
  accordionDetails: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    minHeight: 0,
    paddingTop: 0,
    paddingBottom: theme.spacing(0.75),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    overflow: "hidden",
    borderBottomLeftRadius: 14,
    borderBottomRightRadius: 14,
  },
  accordionTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  accordionTitle: {
    fontWeight: 600,
    fontSize: "0.98rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.92rem",
    },
  },
  countPill: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 999,
    padding: theme.spacing(0.25, 1),
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
  },
  list: {
    paddingTop: 0,
    paddingBottom: 0,
    "& > *:last-child": {
      marginBottom: theme.spacing(1.25),
    },
  },
  listScroll: {
    flex: "1 1 auto",
    minHeight: 0,
    maxHeight: "clamp(180px, 36vh, 320px)",
    overflowY: "auto",
    overscrollBehavior: "contain" as const,
    WebkitOverflowScrolling: "touch",
    paddingRight: theme.spacing(0.5),
    [theme.breakpoints.down("sm")]: {
      maxHeight: "clamp(160px, 34vh, 280px)",
    },
  },
  nestedTitle: {
    marginLeft: 6,
    marginBottom: 4,
    marginTop: 8,
    fontWeight: 600,
  },
  nestedBlock: {
    "&:first-child $nestedTitle": {
      marginTop: 0,
    },
  },
  emptyBlock: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 10,
    padding: theme.spacing(1, 1.25),
    margin: theme.spacing(0.5, 0, 1),
  },
  listItemSelected: {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: theme.palette.common.white,
    "&.Mui-disabled": {
      opacity: 0.5,
    },
  },
  dialogPaper: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "100%",
  },
}));

interface PickerProps {
  label: string;
  options: Options[];
  selectedId?: string;
  selectedTitle?: string;
  onChange?(option: Option): void;
  /** Rendered in the scrollable body (e.g. search). */
  dialogTopContent?: ReactNode;
  /** Called when the dialog opens. */
  onOpen?: () => void;
  /** Header title inside the dialog. */
  dialogTitle?: string;
  /** Auto-open a panel index when results change (search UX). */
  preferredExpandedPanel?: number | false;
}

export const Picker = (props: PickerProps) => {
  const { dialogTitle, dialogTopContent, label, onChange, onOpen, options, preferredExpandedPanel, selectedId, selectedTitle } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });

  const [open, setOpen] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [pending, setPending] = useState<Option | null>(null);

  const openDialog = useCallback(() => {
    setPending(findOptionById(options, selectedId));
    if (preferredExpandedPanel === false) setExpanded(false);
    else if (typeof preferredExpandedPanel === "number") setExpanded(`panel${preferredExpandedPanel}`);
    else setExpanded(false);
    setOpen(true);
    onOpen?.();
  }, [onOpen, options, preferredExpandedPanel, selectedId]);

  useEffect(() => {
    if (!open) return;
    if (preferredExpandedPanel === false) {
      setExpanded(false);
      return;
    }
    if (typeof preferredExpandedPanel === "number") {
      setExpanded(`panel${preferredExpandedPanel}`);
    }
  }, [open, preferredExpandedPanel]);

  const closeDialog = useCallback(() => {
    setOpen(false);
  }, []);

  const handleNext = useCallback(() => {
    if (pending && pending.active !== false) {
      onChange && onChange(pending);
    }
    setOpen(false);
  }, [onChange, pending]);

  const handleChange = (panel: string) => (_: any, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const titleText = dialogTitle || "Select";

  const isSelectedPending = (opt: Option) => pending?.id === opt.id;

  const nextDisabled = !pending || pending.active === false;

  const renderItem = (opt: Option) => (
    <ListItem
      disabled={opt.active === false}
      key={opt.id}
      button
      onClick={() => setPending(opt)}
      className={isSelectedPending(opt) ? classes.listItemSelected : undefined}
    >
      {opt.title}
    </ListItem>
  );

  const getOptionCount = (opt: Options): number => {
    const nested = (opt.nestedList || []).reduce((sum, n) => sum + n.data.length, 0);
    return opt.options.length + nested;
  };

  return (
    <>
      <Button
        fullWidth
        variant="outlined"
        color="primary"
        className={classes.trigger}
        onClick={openDialog}
      >
        <Typography component="span" style={{ textAlign: "left", width: "100%" }}>
          {selectedTitle || label}
        </Typography>
      </Button>
      <Dialog
        open={open}
        onClose={closeDialog}
        fullScreen={isMobile}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{
          className: `${classes.dialogPaper} ${classes.paperDesktop}`.trim(),
        }}
      >
        <AppBar position="static" color="default" elevation={0} className={classes.appBar}>
          <Toolbar variant="dense" className={classes.toolbar}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={closeDialog}
              aria-label="close"
            >
              <Close />
            </IconButton>
            <Typography variant="subtitle1" component="h2" className={classes.title}>
              {titleText}
            </Typography>
            <div style={{ width: 40 }} />
          </Toolbar>
        </AppBar>

        <DialogContent className={classes.content}>
          {dialogTopContent}
          <div className={classes.accordionStack}>
          {options.map((opt, i) => (
            <Accordion
              key={opt.title}
              expanded={expanded === `panel${i}`}
              onChange={handleChange(`panel${i}`)}
              className={`${classes.accordion} ${expanded === `panel${i}` ? classes.accordionExpanded : ""}`.trim()}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                <div className={classes.accordionTitleRow}>
                  <Typography variant="subtitle1" color="primary" className={classes.accordionTitle}>
                    {opt.title}
                  </Typography>
                </div>
                <Typography component="span" className={classes.countPill}>
                  {getOptionCount(opt)}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <div className={classes.listScroll}>
                <List className={classes.list}>
                  {opt.nestedList &&
                    opt.nestedList.map((nested, j) => (
                      <div key={nested.title} className={classes.nestedBlock}>
                        <Typography variant="subtitle2" color="textSecondary" className={classes.nestedTitle}>
                          {j + 1}. {nested.title}
                        </Typography>
                        {nested.data.map(renderItem)}
                      </div>
                    ))}

                  {opt.options.map(renderItem)}
                  {getOptionCount(opt) === 0 ? (
                    <div className={classes.emptyBlock}>
                      <Typography variant="body2" color="textSecondary">
                        No matching rules in this section.
                      </Typography>
                    </div>
                  ) : null}
                </List>
                </div>
              </AccordionDetails>
            </Accordion>
          ))}
          </div>
        </DialogContent>

        <DialogActions className={classes.actions}>
          <Button color="default" onClick={closeDialog}>
            Cancel
          </Button>
          <Button color="primary" variant="contained" onClick={handleNext} disabled={nextDisabled}>
            Next
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
